/* eslint-disable no-unused-vars */
/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import React, {useEffect, useState} from 'react';
// import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import { FormattedMessage } from 'react-intl';
import JSG from '@cedalo/jsg-ui';
import SettingsPaper from '../../utils/SettingsPaper';
import { FormGroup, ListItem, ListItemText, Paper } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { numberFormatTemplates } from '../../../languages/NumberFormatTemplates';
import { intl } from '../../../helper/IntlGlobalProvider';
import { NumberFormatter } from '@cedalo/number-format';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {graphManager} from '../../../GraphManager';
import {useJSGNotification} from '../../utils/JSGHooks';


export function NumberFormatSettings(props) {
	const [category, setCategory] = useState('general');
	const [format, setFormat] = useState(0);
	const [thousands, setThousands] = useState(false);
	const [decimals, setDecimals] = useState(0);
	const [currency, setCurrency] = useState('€');
	const [region, setRegion] = useState('en');
	const [custom, setCustom] = useState('');
	const [customError, setCustomError] = useState();
	const [numberFormats] = useState([]);
	const [value, setValue] = useState([]);

	// eslint-disable-next-line no-shadow
	const addNumberTemplates = (key, val, thousands, decimals, currency, region) => {
		numberFormats.length = 0;

		switch (key) {
		case 'general':
			numberFormats.push({
				id: 0,
				v: val,
				format: 'General',
				formattedvalue: NumberFormatter.formatNumber('General', val, 'general')
			});
			break;
		case 'number':
			for (let i = 0; i < 4; i += 1) {
				const num = JSG.Numbers.isNumber(val) ? val : 1234.56;
				numberFormats.push(
					numberFormatTemplates.getNegativeNumberTemplates(
						thousands,
						Number(decimals),
						i % 2 ? 'red' : undefined,
						num,
						i,
						undefined,
						i
					)
				);
			}
			break;
		case 'currency':
			for (let i = 0; i < 4; i += 1) {
				const num = JSG.Numbers.isNumber(val) ? val : 1234.56;
				numberFormats.push(
					numberFormatTemplates.getNegativeNumberTemplates(
						thousands,
						Number(decimals),
						i % 2 ? 'red' : undefined,
						num,
						i,
						currency,
						i
					)
				);
			}
			break;
		case 'date': {
			let id = 0;
			const templates = numberFormatTemplates.getTemplates(region);
			if (templates) {
				const num = JSG.Numbers.isNumber(val) ? val : 44635;
				templates.Date.forEach((item) => {
					numberFormats.push({
						id,
						v: num,
						format: item.t,
						formattedvalue: NumberFormatter.formatNumber(item.t, num, 'date')
					});
					id += 1;
				});
			}
			break;
		}
		case 'time': {
			let id = 0;
			const templates = numberFormatTemplates.getTemplates(region);
			if (templates) {
				const num = JSG.Numbers.isNumber(val) ? val : 44635;
				templates.Time.forEach((item) => {
					numberFormats.push({
						id,
						v: num,
						format: item.t,
						formattedvalue: NumberFormatter.formatNumber(item.t, num, 'time')
					});
					id += 1;
				});
			}
			break;
		}
		case 'percent': {
			const num = JSG.Numbers.isNumber(val) ? val : 1234.56;
			numberFormats.push(numberFormatTemplates.getNumberTemplate(Number(decimals), num, 5, 0));
			break;
		}
		case 'fraction': {
			let id = 0;
			const templates = numberFormatTemplates.getTemplates(region);
			const num = JSG.Numbers.isNumber(val) ? val : 1.5;
			if (templates) {
				templates.Fraction.forEach((item) => {
					numberFormats.push({
						id,
						v: num,
						format: item.t,
						formattedvalue: { formattedValue: intl.formatMessage({ id: `Fraction${id}` }, {}) }
					});
					id += 1;
				});
			}
			break;
		}
		case 'science': {
			const num = JSG.Numbers.isNumber(val) ? val : 44635;
			numberFormats.push(
				numberFormatTemplates.getNumberTemplate(
					Number(decimals),
					num,
					key,
					0
				)
			);
			break;
		}
		case 'text':
			numberFormats.push({
				id: 0,
				v: val,
				format: '@',
				formattedvalue: NumberFormatter.formatNumber('@', val, 'text')
			});
			break;
		case 'custom':
			numberFormats.push({
				id: 0,
				v: val,
				format: custom,
				formattedvalue: NumberFormatter.formatNumber(custom, val, '')
			});
			break;
		default:
			break;
		}
	};

	const applyToSelection = (cat, tds,  newFormat) => {
		const attributesMap = new JSG.Dictionary();

		const fmt = numberFormats[newFormat];
		if (fmt === undefined) {
			return;
		}
		let settings = cat;
		switch (cat) {
		case 'number':
			if (decimals !== undefined && tds !== undefined) {
				settings += `;${decimals};${tds}`;
			}
			break;
		case 'currency':
			if (decimals !== undefined && thousands !== undefined) {
				settings += `;${decimals};${tds};${currency}`;
			}
			break;
		case 'date':
		case 'time':
			if (region !== undefined) {
				settings += `;${region}`;
			}
			break;
		case 'percent':
		case 'science':
			if (decimals !== undefined) {
				settings += `;${decimals}`;
			}
			break;
		default:
			break;
		}

		attributesMap.put(JSG.TextFormatAttributes.NUMBERFORMAT, fmt.format);
		attributesMap.put(JSG.TextFormatAttributes.LOCALCULTURE, settings);

		if (graphManager.getGraphViewer().getSelectionProvider().hasSelection()) {
			graphManager.getGraphEditor().getInteractionHandler().applyTextFormatMap(attributesMap);
		} else {

			const cmd = JSG.SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				graphManager.graph.sheetSelection.getRanges(),
				attributesMap
			);
			graphManager.synchronizedExecute(cmd);
		}

	}

	const handleFormat = (cat, key) => {
		if (cat === undefined || key === undefined) {
			return;
		}

		setFormat(key);
		applyToSelection(cat, thousands, key);
	};

	const onSelectionChange = () => {
		const selection = graphManager.getGraphViewer().getSelection();
		const { graph } = graphManager;
		if (!graph) {
			return;
		}

		let val = 1234.56;
		let textFormat;

		if (selection.length) {
			if (selection[0].getModel() instanceof JSG.SheetPlotNode && selection[0].getView().chartSelection) {
				textFormat = selection[0].getView().getSelectedTextFormat();
			} else {
				textFormat = JSG.TextFormatAttributes.retainFromSelection(selection);
			}
		} else {
			const cell = graphManager.graph.sheetSelection.activeCellData;
			const pos = graphManager.graph.sheetSelection.activeCell;
			if (cell && pos) {
				if (cell.value !== undefined) {
					val = cell.value;
				}
				textFormat = graphManager.graph.sheetSelection.sheet.getTextFormatProperties(pos.x, pos.y, true);
			}
		}

		if (textFormat) {
			const nf = textFormat.numberformat;
			const lc = textFormat.localculture
			if (!lc) {
				return;
			}
			let cat = 'general';
			let cur = '€';
			let tds = false;
			let decs = 0;
			let reg = props.locale;

			const sections = lc.split(';');
			if (sections.length) {
				cat = sections[0];
				switch (cat) {
				case 'number':
					decs = sections.length > 1 ? Number(sections[1]) : 0;
					tds = sections[2] === 'true';
					break;
				case 'currency':
					decs = sections.length > 1 ? Number(sections[1]) : 0;
					tds = sections[2] === 'true';
					cur = sections.length > 3 ? sections[3] : '€';
					break;
				case 'date':
				case 'time':
					reg = sections[1];
					break;
				case 'percent':
				case 'science':
					decs = sections.length > 1 ? Number(sections[1]) : 0;
					break;
				default:
					break;
				}
			}

			setValue(val);
			setRegion(reg);
			setCategory(cat);
			setDecimals(decs);
			setThousands(tds);
			setCurrency(cur);
			setCustom(nf);

			addNumberTemplates(cat, val, tds, decs, cur, reg);

			switch (cat) {
			case 'number':
			case 'currency':
			case 'date':
			case 'time':
			case 'fraction':
				numberFormats.forEach((fmt, index) => {
					if (fmt.format === nf) {
						setFormat(index);
					}
				});
				break;
			default:
				setFormat(0);
				break;
			}
			return;
		}
		addNumberTemplates('general', 1234.56, false, 0, '€', props.locale);
		setFormat(0);
		setValue(1234.56);
		setRegion(props.locale);
		setCategory('general');
		setDecimals(0);
		setThousands(false);
		setCurrency('€');
		setCustom('');
	}

	useJSGNotification(JSG.SelectionProvider.SELECTION_CHANGED_NOTIFICATION, onSelectionChange);
	useJSGNotification(JSG.WorksheetNode.SELECTION_CHANGED_NOTIFICATION, onSelectionChange);
	useEffect(() => {
		onSelectionChange();
	}, []);


	const handleCategoryChange = (event) => {
		const cat = event.target.value;

		setCustom(numberFormats[format].format)

		if (cat !== 'custom') {
			addNumberTemplates(cat, value, false, 0, '€', props.locale);
		}
		handleFormat(cat, 0);

		setCategory(cat);
		setThousands(false);
		setDecimals(0);
		setCurrency('€');
		setRegion(props.locale);

	};

	const handleDecimals = (event) => {
		setDecimals(event.target.value);
		addNumberTemplates(category, value, thousands, event.target.value, currency, region);
		applyToSelection(category, thousands, format);
	};

	const handleCurrency = (event) => {
		setCurrency(event.target.value);
		addNumberTemplates(category, value, thousands, decimals, event.target.value, region);
		applyToSelection(category, thousands, format);
	};

	const handleRegion = (event) => {
		setRegion(event.target.value);
		addNumberTemplates(category, value, thousands, decimals, currency, event.target.value);
		applyToSelection(category, thousands, format);
	};

	const handleThousand = (event, state) => {
		setThousands(state);
		addNumberTemplates(category, value, state, decimals, currency, region);
		applyToSelection(category, state, format);
	};
	const handleCustomChange = (event) => {
		setCustom(event.target.value)
		const result = NumberFormatter.formatNumber(event.target.value, value, '');
		if (result.message) {
			setCustomError(result.message);
		} else {
			setCustomError();
		}
	};

	const handleCustomBlur = (event) => {
		const result = NumberFormatter.formatNumber(event.target.value, value, '');
		if (result.message) {
			setCustomError(result.message);
		} else {
			setCustomError();
			applyToSelection(category, thousands, format);
		}
	};

	let sample = '';
	let sampleColor = 'black';

	if (category !== undefined && format !== undefined) {
		addNumberTemplates(category, value, thousands, decimals, currency, region);
		const fmt = numberFormats[format];
		if (fmt !== undefined) {
			const result = NumberFormatter.formatNumber(fmt.format, value, category);
			sample = result.formattedValue;
			sampleColor = result.color;
		}
	}

	if (!props.open) {
		return null;
	}

	return (
		<SettingsPaper
			open={props.open}
			close={props.close}
			helpContext="numberformatsettings"
			title={<FormattedMessage id="NumberFormatSettings.Title" defaultMessage="Number Format" />}
		>
			<FormGroup>
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					select
					value={category}
					onChange={handleCategoryChange}
					label={<FormattedMessage id="NumberFormatSettings.Category" defaultMessage="Category" />}
				>
					<MenuItem value="general" key="general">
						<FormattedMessage id="NumberFormatSettings.General" defaultMessage="General" />
					</MenuItem>
					<MenuItem value="number" key="number">
						<FormattedMessage id="NumberFormatSettings.Number" defaultMessage="Number" />
					</MenuItem>
					<MenuItem value="currency" key="currency">
						<FormattedMessage id="NumberFormatSettings.Currency" defaultMessage="Currency" />
					</MenuItem>
					<MenuItem value="date" key="date">
						<FormattedMessage id="NumberFormatSettings.Date" defaultMessage="Date" />
					</MenuItem>
					<MenuItem value="time" key="time">
						<FormattedMessage id="NumberFormatSettings.Time" defaultMessage="Time" />
					</MenuItem>
					<MenuItem value="percent" key="percent">
						<FormattedMessage id="NumberFormatSettings.Percent" defaultMessage="Percent" />
					</MenuItem>
					<MenuItem value="fraction" key="fraction">
						<FormattedMessage id="NumberFormatSettings.Fraction" defaultMessage="Fraction" />
					</MenuItem>
					<MenuItem value="science" key="science">
						<FormattedMessage id="NumberFormatSettings.Scientific" defaultMessage="Scientific" />
					</MenuItem>
					<MenuItem value="text" key="text">
						<FormattedMessage id="NumberFormatSettings.Text" defaultMessage="Text" />
					</MenuItem>
					<MenuItem value="custom" key="custom">
						<FormattedMessage id="NumberFormatSettings.Custom" defaultMessage="Custom" />
					</MenuItem>
				</TextField>
				{category === 'currency' && (
					<TextField
						variant="outlined"
						size="small"
						margin="normal"
						select
						label={
							<FormattedMessage id="NumberFormatSettings.CurrencySymbol" defaultMessage="Currency Symbol" />
						}
						value={currency}
						onChange={(event) => handleCurrency(event)}
					>
						<MenuItem value="€" key="€">
							€
						</MenuItem>
						<MenuItem value="$" key="$">
							$
						</MenuItem>
						<MenuItem value="£" key="£">
							£
						</MenuItem>
					</TextField>
				)}
				{(category === 'date' || category === 'time') && (
					<TextField
						variant="outlined"
						size="small"
						margin="normal"
						fullSize
						select
						label="Region"
						value={region}
						onChange={(event) => handleRegion(event)}
					>
						<MenuItem value="de" key="de">
							{intl.formatMessage({ id: 'NumberFormatSettings.Germany' }, {})}
						</MenuItem>
						<MenuItem value="us" key="us">
							{intl.formatMessage({ id: 'NumberFormatSettings.EnglishUS' }, {})}
						</MenuItem>
						<MenuItem value="en" key="en">
							{intl.formatMessage({ id: 'NumberFormatSettings.EnglishUK' }, {})}
						</MenuItem>
					</TextField>
				)}
				{(category === 'number' ||
					category === 'currency' ||
					category === 'percent' ||
					category === 'science') && (
					<TextField
						variant="outlined"
						size="small"
						margin="normal"
						id="number"
						label={<FormattedMessage id="NumberFormatSettings.Decimals" defaultMessage="Decimals" />}
						inputProps={{
							min: 0,
							max: 10,
							step: 1,
							style: { fontSize: '0.85rem' }
						}}
						value={decimals}
						onChange={(event) => handleDecimals(event)}
						type="number"
					/>
				)}
				{(category === 'number' || category === 'currency') && (
					<FormControlLabel
						control={
							<Checkbox checked={thousands} onChange={(event, state) => handleThousand(event, state)} />
						}
						label={<FormattedMessage id="NumberFormatSettings.Thousands" defaultMessage="Thousands" />}
					/>
				)}
				{numberFormats.length > 1 ? (
					<Paper
						variant="outlined"
						style={{
							marginTop: '10px',
							height: '200px',
							overflowY: 'scroll'
						}}
					>
						<List dense disablePadding>
							{numberFormats.map((row) => (
								<ListItem
									key={row.id}
									selected={row.id === format}
									onClick={() => handleFormat(category, row.id)}
								>
									<ListItemText
										style={{
											color: row.color !== undefined ? row.color : undefined,
											display: 'flex',
											cursor: 'pointer'
										}}
									>
										{row.formattedvalue ? row.formattedvalue.formattedValue : ''}
									</ListItemText>
								</ListItem>
							))}
						</List>
					</Paper>
				) : null}
				{category === 'custom' ? (
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					label={<FormattedMessage id="NumberFormatSettings.CustomLabel" defaultMessage="Custom Numberformat" />}
					error={customError !== undefined}
					spellCheck={false}
					helperText={customError}
					value={custom}
					onChange={(event) => handleCustomChange(event)}
					onBlur={(event) => handleCustomBlur(event)}
				/>) : null}
				<TextField
					variant="outlined"
					size="small"
					margin="normal"
					label={<FormattedMessage id="NumberFormatSettings.Example" defaultMessage="Example" />}
					InputProps={{
						readOnly: true,
						sx: {
							color: sampleColor,
						}
					}}
					value={sample}
					// onChange={(event) => {
				/>
			</FormGroup>
		</SettingsPaper>
	);
}
